import { Link, useParams } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import { usePortalAuth } from "@/context/PortalAuth";
import { FileText, ScrollText, MessageSquare, ClipboardList, ArrowRight, Home } from "lucide-react";

export default function PortalDashboard() {
  const { slug } = useParams();
  const { me } = usePortalAuth();
  const person = me?.person;
  const isTenant = (person?.roles || []).includes("Tenant");
  const units = [...(me?.owned_units || []), ...(me?.occupied_units || [])];

  return (
    <PortalLayout subtitle="Home" title={`Namaste, ${person?.name || "there"}`}>
      {/* Broadcast alerts placeholder — could be pulled dynamically in future */}
      {/* Units */}
      <section className="mb-8">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Your units</div>
        <div className="grid md:grid-cols-2 gap-4">
          {units.length === 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 text-stone-500">No units linked to your profile.</div>
          )}
          {units.map((u) => (
            <div key={u.id} className="bg-white border border-stone-200 rounded-2xl p-6" data-testid={`unit-card-${u.id}`}>
              <div className="text-xs uppercase tracking-widest text-terracotta font-semibold flex items-center gap-1"><Home size={12}/> {u.unit_type}</div>
              <div className="editorial text-3xl mt-2 leading-tight">{u.building}{u.wing ? ` / ${u.wing}` : ""} / {u.unit_no}</div>
              <div className="text-sm text-stone-500 mt-1">Floor {u.floor || "—"} · {u.area_sqft || "—"} sqft</div>
              <div className="text-xs text-stone-500 mt-4">House tax: {u.house_tax_no || "—"}</div>
              <div className="text-xs text-stone-500">Parkings: {u.parking_count ?? 0}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Quick access</div>
        <div className="grid md:grid-cols-2 gap-3">
          {!isTenant && <QuickCard to={`/portal/${slug}/invoices`} icon={FileText} title="Invoices" body="View and download PDF invoices for your unit." testid="q-invoices"/>}
          <QuickCard to={`/portal/${slug}/notices`} icon={ScrollText} title="Notices" body="Meeting notices, minutes, and society-wide announcements." testid="q-notices"/>
          <QuickCard to={`/portal/${slug}/complaints`} icon={MessageSquare} title="Complaints" body="Raise an issue about your unit or common areas." testid="q-complaints"/>
          {!isTenant && <QuickCard to={`/portal/${slug}/applications`} icon={ClipboardList} title="Applications" body="Statement of dues, NOC, transfer, or work permissions." testid="q-applications"/>}
        </div>
      </section>
    </PortalLayout>
  );
}

function QuickCard({ to, icon: Icon, title, body, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group bg-white border border-stone-200 rounded-2xl p-6 hover:border-terracotta transition-colors flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
        <Icon size={20} strokeWidth={1.5}/>
      </div>
      <div className="flex-1">
        <div className="editorial text-xl">{title}</div>
        <div className="text-sm text-stone-600 mt-1">{body}</div>
      </div>
      <ArrowRight size={18} className="text-stone-400 group-hover:text-terracotta group-hover:translate-x-0.5 transition-all mt-1"/>
    </Link>
  );
}
